import React, { useState, useEffect } from 'react';
import { getAdminAnalytics } from '../../api'; // Adjust path
// Use Recharts imports
import { 
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts'; 
import { UserGroupIcon, BriefcaseIcon, ArchiveBoxArrowDownIcon, CurrencyDollarIcon, ShoppingCartIcon, CreditCardIcon } from '@heroicons/react/24/outline'; // Removed ArrowLeftIcon
import Breadcrumbs from '../../components/Breadcrumbs'; // Import Breadcrumbs

// --- Chart Styling & Formatting Constants ---
const gridStyle = { stroke: '#4b5563' }; // gray-600
const axisStyle = { fontSize: '0.75rem', fill: '#9ca3af' }; // text-xs text-gray-400
const tickLineStyle = { stroke: '#6b7280' }; // gray-500
const legendStyle = { fontSize: '0.75rem', color: '#9ca3af' }; // text-xs text-gray-400

// --- Chart Formatting Functions ---
const formatDateTick = (tickItem) => {
  // Assuming tickItem is a timestamp
  const date = new Date(tickItem);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatTooltipLabel = (label) => {
  // Assuming label is a timestamp
  const date = new Date(label);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// --- Job Status Constants ---
// Define based on expected statuses from API. Adjust if needed.
const jobStatuses = ['SUCCESS', 'FAILED', 'PENDING', 'PROCESSING'];
const jobStatusColors = {
  SUCCESS: '#34d399', // emerald-400
  FAILED: '#f87171', // red-400
  PENDING: '#fbbf24', // amber-400
  PROCESSING: '#60a5fa' // blue-400
};

// Re-use formatBytes helper
const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0 || !bytes) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

// Custom Tooltip for Recharts
const CustomTooltip = ({ active, payload, label, labelFormatter }) => {
  if (active && payload && payload.length) {
    const formattedLabel = labelFormatter ? labelFormatter(label) : label;
    return (
      <div className="bg-gray-700 p-2 rounded shadow-lg border border-gray-600 text-sm">
        <p className="label text-gray-300">{`${formattedLabel}`}</p>
        {payload.map((entry, index) => (
           <p key={`item-${index}`} style={{ color: entry.color || entry.stroke }}>
             {`${entry.name}: ${entry.value}`}
           </p>
        ))}
      </div>
    );
  }
  return null;
};

const AdminAnalyticsPage = () => {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rangeDays, setRangeDays] = useState(30); // default 30d

    useEffect(() => {
        const fetchAnalytics = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getAdminAnalytics(rangeDays);
                // Process user growth data
                const processedUserGrowth = data.user_growth_daily?.map(d => ({
                    ...d,
                    date: new Date(d.date).getTime()
                })) || [];

                // Process job trends data - transform from [{date, status, count}] to [{date, SUCCESS: X, FAILED: Y, ...}]
                const jobTrendsByDate = {};
                data.job_trends_daily?.forEach(item => {
                    const timestamp = new Date(item.date).getTime();
                    if (!jobTrendsByDate[timestamp]) {
                        jobTrendsByDate[timestamp] = { date: timestamp };
                        jobStatuses.forEach(status => {
                            jobTrendsByDate[timestamp][status] = 0;
                        });
                    }
                    jobTrendsByDate[timestamp][item.status] = item.count;
                });
                const processedJobTrends = Object.values(jobTrendsByDate);

                // Process moving average data
                const processedMovingAvg = data.job_success_moving_avg?.map(d => ({
                    date: new Date(d.date).getTime(),
                    moving_avg: parseFloat(d.moving_avg.toFixed(2))
                })) || [];

                const processedData = {
                    ...data,
                    user_growth_daily: processedUserGrowth,
                    job_trends_daily: processedJobTrends,
                    job_success_moving_avg: processedMovingAvg
                };
                setAnalyticsData(processedData);
            } catch (err) {
                console.error('Failed to fetch analytics:', err);
                setError(err.message || 'Failed to load analytics.');
            } finally {
                setLoading(false);
            }
        };
        fetchAnalytics();
    }, [rangeDays]);

    const crumbs = [
        { label: 'Admin', to: '/app/admin' },
        { label: 'Analytics' } // Current page
    ];

    return (
        <div className="text-foreground">
            <Breadcrumbs crumbs={crumbs} />

            {/* Conditional rendering for loading state */}
            {loading && <div className="text-foreground">Loading analytics...</div>}

            {/* Conditional rendering for error state */}
            {error && <div className="text-danger">Error: {error}</div>}

            {/* Conditional rendering for no data */}
            {!loading && !error && !analyticsData && <div className="text-foreground">No analytics data available.</div>}

            {/* Main content shown only when not loading, no error, and data exists */}
            {!loading && !error && analyticsData && (
                <>
                    <h1 className="text-2xl font-semibold mb-6">Analytics</h1>

                    {/* Range Picker */}
                    <div className="flex items-center gap-2 mb-6">
                        {[14,30,60,90,180].map(d => (
                            <button
                                key={d}
                                onClick={() => setRangeDays(d)}
                                className={`px-3 py-1 rounded text-sm border transition-colors ${rangeDays === d ? 'bg-accent text-white border-accent' : 'bg-background-elevated border-border text-foreground hover:border-accent'}`}
                            >{d}d</button>
                        ))}
                    </div>

                    {/* Summary Cards - Updated to 2 rows */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                         {/* Row 1 */}
                         {/* Card 1: Total Users */}
                        <div className="bg-background-elevated border border-border p-6 rounded-lg shadow flex items-center">
                            <UserGroupIcon className="w-10 h-10 text-accent mr-4"/>
                            <div>
                                <div className="text-sm text-muted-foreground">Total users</div>
                                <div className="text-2xl font-bold">{analyticsData.summary?.total_users ?? 'N/A'}</div>
                            </div>
                        </div>
                         {/* Card 2: Total Jobs */}
                         <div className="bg-background-elevated border border-border p-6 rounded-lg shadow flex items-center">
                            <BriefcaseIcon className="w-10 h-10 text-accent mr-4"/>
                            <div>
                                <div className="text-sm text-muted-foreground">Total jobs processed</div>
                                <div className="text-2xl font-bold">{analyticsData.summary?.total_jobs ?? 'N/A'}</div>
                            </div>
                        </div>
                         {/* Card 3: Space Saved */}
                         <div className="bg-background-elevated border border-border p-6 rounded-lg shadow flex items-center">
                            <ArchiveBoxArrowDownIcon className="w-10 h-10 text-accent mr-4"/>
                            <div>
                                <div className="text-sm text-muted-foreground">Total space saved</div>
                                <div className="text-2xl font-bold">{formatBytes(analyticsData.summary?.total_bytes_saved ?? 0)}</div>
                            </div>
                        </div>
                    </div>
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        {/* Row 2 */}
                        {/* Card 4: Total Revenue */}
                        <div className="bg-background-elevated border border-border p-6 rounded-lg shadow flex items-center">
                            <CurrencyDollarIcon className="w-10 h-10 text-success mr-4"/>
                            <div>
                                <div className="text-sm text-muted-foreground">Total revenue</div>
                                {/* Assuming currency is USD, adjust formatting as needed */}
                                <div className="text-2xl font-bold">${analyticsData.summary?.total_revenue?.toFixed(2) ?? '0.00'}</div>
                            </div>
                        </div>
                         {/* Card 5: Transactions */}
                         <div className="bg-background-elevated border border-border p-6 rounded-lg shadow flex items-center">
                            <ShoppingCartIcon className="w-10 h-10 text-success mr-4"/>
                            <div>
                                <div className="text-sm text-muted-foreground">Successful transactions</div>
                                <div className="text-2xl font-bold">{analyticsData.summary?.total_successful_transactions ?? '0'}</div>
                            </div>
                        </div>
                         {/* Card 6: Credits Purchased */}
                         <div className="bg-background-elevated border border-border p-6 rounded-lg shadow flex items-center">
                            <CreditCardIcon className="w-10 h-10 text-success mr-4"/>
                            <div>
                                <div className="text-sm text-muted-foreground">Total credits purchased</div>
                                <div className="text-2xl font-bold">{analyticsData.summary?.total_credits_purchased ?? '0'}</div>
                            </div>
                        </div>
                    </div>

                    {/* Charts - Adjusted grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* User Growth Chart (Span 1 col)*/}
                        <div className="lg:col-span-1 bg-background-elevated border border-border p-6 rounded-lg shadow" style={{ height: '400px' }}>
                            <h2 className="text-xl font-semibold mb-4 text-muted-foreground">New users (last {rangeDays} days)</h2>
                            {analyticsData.user_growth_daily.length > 0 ? (
                                <ResponsiveContainer width="100%" height="90%"> 
                                     <LineChart data={analyticsData.user_growth_daily} margin={{ top: 5, right: 20, left: 10, bottom: 20 }}>
                                        <defs>
                                            <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#818cf8" stopOpacity={0.8}/>
                                                <stop offset="95%" stopColor="#818cf8" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke={gridStyle.stroke} />
                                        <XAxis 
                                            dataKey="date" 
                                            type="number" 
                                            scale="time" 
                                            domain={['dataMin', 'dataMax']}
                                            tickFormatter={formatDateTick} 
                                            stroke={axisStyle.fill}
                                            tick={axisStyle}
                                            tickLine={tickLineStyle}
                                        />
                                        <YAxis 
                                            stroke={axisStyle.fill} 
                                            tick={axisStyle} 
                                            tickLine={tickLineStyle}
                                        />
                                        <Tooltip 
                                            content={<CustomTooltip labelFormatter={formatTooltipLabel} />} 
                                        />
                                        <Legend wrapperStyle={{ ...legendStyle, paddingTop: '10px' }} verticalAlign="bottom" align="center" />
                                        <Line 
                                            type="monotone" 
                                            dataKey="count" 
                                            name="New users" 
                                            stroke="#818cf8" 
                                            strokeWidth={2} 
                                            dot={false} 
                                            fillOpacity={1} 
                                            fill="url(#colorUsers)" 
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : <p className="text-muted-foreground flex items-center justify-center h-full">No user growth data available.</p>}
                        </div>
                        {/* Job Trends Chart (Span 1 col) */}
                         <div className="lg:col-span-1 bg-background-elevated border border-border p-6 rounded-lg shadow" style={{ height: '400px' }}>
                             <h2 className="text-xl font-semibold mb-4 text-muted-foreground">Job status trends (last {rangeDays} days)</h2>
                             {analyticsData.job_trends_daily.length > 0 ? (
                                <ResponsiveContainer width="100%" height="90%"> 
                                    <BarChart data={analyticsData.job_trends_daily} margin={{ top: 5, right: 20, left: 10, bottom: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={gridStyle.stroke} />
                                         <XAxis 
                                            dataKey="date" 
                                            type="number" 
                                            scale="time" 
                                            domain={['dataMin', 'dataMax']}
                                            tickFormatter={formatDateTick} 
                                            stroke={axisStyle.fill}
                                            tick={axisStyle}
                                            tickLine={tickLineStyle}
                                        />
                                        <YAxis 
                                            stroke={axisStyle.fill} 
                                            tick={axisStyle} 
                                            tickLine={tickLineStyle} 
                                        />
                                        <Tooltip 
                                            content={<CustomTooltip labelFormatter={formatTooltipLabel} />} 
                                        />
                                        <Legend wrapperStyle={{ ...legendStyle, paddingTop: '10px' }} verticalAlign="bottom" align="center"/>
                                        {jobStatuses.map(status => (
                                            <Bar key={status} dataKey={status} stackId="a" fill={jobStatusColors[status] || '#6b7280'} />
                                        ))}
                                    </BarChart>
                                 </ResponsiveContainer>
                            ) : <p className="text-muted-foreground flex items-center justify-center h-full">No job trend data available.</p>}
                        </div>
                        {/* Moving Average Chart (Span 1 col) */}
                        <div className="lg:col-span-1 bg-background-elevated border border-border p-6 rounded-lg shadow" style={{ height: '400px' }}>
                            <h2 className="text-xl font-semibold mb-4 text-muted-foreground">Job count moving average (14d)</h2>
                            {analyticsData.job_success_moving_avg.length > 0 ? (
                                <ResponsiveContainer width="100%" height="90%">
                                    <LineChart data={analyticsData.job_success_moving_avg} margin={{ top: 5, right: 20, left: 10, bottom: 20 }}>
                                        <defs>
                                            <linearGradient id="colorMovingAvg" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.8} />
                                                <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke={gridStyle.stroke} />
                                        <XAxis
                                            dataKey="date"
                                            type="number"
                                            scale="time"
                                            domain={['dataMin', 'dataMax']}
                                            tickFormatter={formatDateTick}
                                            stroke={axisStyle.fill}
                                            tick={axisStyle}
                                            tickLine={tickLineStyle}
                                        />
                                        <YAxis
                                            stroke={axisStyle.fill}
                                            tick={axisStyle}
                                            tickLine={tickLineStyle}
                                        />
                                        <Tooltip content={<CustomTooltip labelFormatter={formatTooltipLabel} />} />
                                        <Legend wrapperStyle={{ ...legendStyle, paddingTop: '10px' }} verticalAlign="bottom" align="center" />
                                        <Line
                                            type="monotone"
                                            dataKey="moving_avg"
                                            name="14d Moving Avg (outlier-filtered)"
                                            stroke="#60a5fa"
                                            strokeWidth={2}
                                            dot={false}
                                            fillOpacity={1}
                                            fill="url(#colorMovingAvg)"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : <p className="text-muted-foreground flex items-center justify-center h-full">No moving average data available.</p>}
                        </div>
                    </div>
                    
                </>
            )}
        </div>
    );
};

export default AdminAnalyticsPage; 